/* MoveVeilRenderer.js */

"use strict";
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const InputTextControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputText;
const InputRadioControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single
    .InputRadio;
const ComboBoxControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single
    .ComboBox;
const _ = require("lodash");

class MoveVeilRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("move-plan-gl");
        this._rootElem.component = this._model;

        this._titleComponent = document.createElement("h2");
        this._titleComponent.innerText = "Move to a different plan";

        this._totalDevice = document.createElement("div");
        this._totalDevice.classList.add("total-device");
        this._totalDevice.innerText = `Total Device : ${this._model._devices.length}`;
        this._footerContainer = document.createElement("div");
        this._footerContainer.classList.add("footer-container");

        this._subheadLabel = document.createElement("h4");
        this._subheadLabel.innerText = "Select the Plan:";

        this._newPlan = new InputTextControl({
            placeholder: "Enter a new plan name",
            disabled: false,
        });

        this._plansContainer = document.createElement("div");
        this._plansContainer.classList.add("plans-container");

        this._plans = new InputRadioControl({
            horizontal: false,
            options: [
                {
                    label: "New Plan",
                    value: "new",
                },
                {
                    label: "Existing Plan",
                    value: "existing",
                },
            ],
            value: "new",
        });

        this._plansValueContainer = document.createElement("div");
        this._plansValueContainer.classList.add("plans-value-container");

        this._existingPlan = new ComboBoxControl({
            options: [],
            showFilter: true,
            disabled: true,
        });

        this._confirmButton = new ButtonControl({
            label: "Move",
            darkTheme: true,
            disabled: true,
        });
        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true,
        });
        this._buttonContainer = document.createElement("button-container");

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._plans.on(
            "UserValue",
            (this._togglePlanAvailabilityAction = this._togglePlanAvailabilityAction.bind(this))
        );
        this._confirmButton.on(
            "UserClick",
            (this._onConfirmButtonClick = this._onConfirmButtonClick.bind(this))
        );
        this._cancelButton.on(
            "UserClick",
            (this._onCancelButtonClick = this._onCancelButtonClick.bind(this))
        );
        this._newPlan.on(
            "UserKeyDown",
            (this._checkForImpliedConfirm = this._checkForImpliedConfirm.bind(this))
        );
        this._newPlan.on("UserValue", (this.checkValue = this.checkValue.bind(this)));
        this._existingPlan.on(
            "UserValue",
            (this.checkExistingValue = this.checkExistingValue.bind(this))
        );
    }

    _unregisterListeners() {
        this._plans.removeAllListeners("UserValue");
        this._confirmButton.removeAllListeners("UserClick");
        this._cancelButton.removeAllListeners("UserClick");
        this._newPlan.removeAllListeners("UserKeyDown");
        this._newPlan.removeAllListeners("UserValue");
        this._existingPlan.removeAllListeners("UserValue");
    }

    _onConfirmButtonClick() {
        this._moveToPlan();
    }
    _onCancelButtonClick() {
        this._model.cancelMove({userRejected: true});
    }

    _checkForImpliedConfirm(event) {
        if (event.keyCode === 13) {
            if (!_.isEmpty(this._newPlan.getValue())) {
                this._moveToPlan();
            }
        }
    }

    checkValue(value) {
        this._confirmButton.disabled = _.isEmpty(value) || value.trim().length === 0;
    }

    checkExistingValue(value) {
        this._confirmButton.disabled = _.isEmpty(value) || value.trim().length === 0;
    }
    _togglePlanAvailabilityAction(newValue) {
        this._existingPlan.setValue();
        this._newPlan.setValue();
        this.checkValue("");
        this.checkExistingValue("");
        if (newValue === "new") {
            this._newPlan.setDisabled(false);
            this._existingPlan.setDisabled(true);
        } else {
            this._existingPlan.setDisabled(false);
            this._newPlan.setDisabled(true);
            this._model.getPlan();
        }
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        let promArr = [this._newPlan.shutdown(), this._confirmButton.shutdown()];

        return Promise.all(promArr).then(
            () => {
                this._newPlan = undefined;
                this._confirmButton = undefined;
                this._cancelButton = undefined;
                this._existingPlan = undefined;
                this._footerContainer.remove();
                this._footerContainer = undefined;
                this._plans = undefined;
                this._plansValueContainer = undefined;
                this._plansContainer.remove();
                this._plansContainer = undefined;
                this._subheadLabel = undefined;
                this._buttonContainer.remove();
                this._buttonContainer = undefined;
                this._titleComponent.remove();
                this._titleComponent = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this._model.logger.warn(err, "MoveVeilRenderer.shutdown() error");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return new Promise((resolve /*,reject*/) => {
            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._titleComponent);
                this._rootElem.appendChild(this._subheadLabel);
                this._plansContainer.appendChild(this._plans.renderer.rootElement);
                this._plansValueContainer.appendChild(this._newPlan.renderer.rootElement);
                this._plansValueContainer.appendChild(this._existingPlan.renderer.rootElement);
                this._plansContainer.appendChild(this._plansValueContainer);
                this._rootElem.appendChild(this._plansContainer);
                this._buttonContainer.appendChild(this._cancelButton.renderer.rootElement);
                this._buttonContainer.appendChild(this._confirmButton.renderer.rootElement);
                this._footerContainer.appendChild(this._totalDevice);
                this._footerContainer.appendChild(this._buttonContainer);
                this._rootElem.appendChild(this._footerContainer);
                setTimeout(() => {
                    this._newPlan.focus();
                });
            }
            resolve();
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    //TODO Private Render Methods here

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _moveToPlan() {
        const moveType = this._plans.value;
        const planDetails = moveType === "new" ? this._newPlan.value : this._existingPlan.value;
        this._model.moveToPlan(moveType, planDetails);
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = MoveVeilRenderer;
